import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, resolveDynamicComponent as _resolveDynamicComponent, withCtx as _withCtx, normalizeClass as _normalizeClass } from "vue"

import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { IonApp, IonModal, IonRouterOutlet } from '@ionic/vue'
import { setLocale } from '@vee-validate/i18n'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useGtm } from '@gtm-support/vue-gtm'
import { State } from '@/store'
import { useApi } from '@/plugins/api'
import { useMixpanel } from '@/plugins/mixpanel/mixpanel'
import { useGwModal } from '@/plugins/gw-modal'
import { useGrid } from 'vue-screen'
import AddToCalendarModal from '@/components/modals/AddToCalendarModal.vue'
import LoginModal from '@/components/modals/LoginModal.vue'
import MenuModal from '@/components/modals/MenuModal.vue'

import GwBottomSheet from '@/plugins/gw-modal/GwBottomSheet.vue'
import GwModal from '@/plugins/gw-modal/GwModal.vue'
import GwHeader from '@/components/GwHeader.vue'
import RefreshApp from '@/components/RefreshApp.vue'
import SystemDownModal from '@/components/SystemDown.vue'
import { useGWAnalytics } from '@/plugins/analytics'
import { GtmAnalyticsProvider } from '@/plugins/analytics/providers/ga'
import { MixpanelAnalyticsProvider } from '@/plugins/analytics/providers/mixpanel'
import { useGWAnalyticsBasePayload } from '@/plugins/analytics/composables/analyticsBasePayload'
import { GwCustomAnalyticsProvider } from './plugins/analytics/providers/gw'
import { useUtm } from './composables/utm'
import ServerOverloaded from './components/ServerOverloaded.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const store = useStore<State>()
const api = useApi()
const { locale } = useI18n()
const route = useRoute()
const { utm } = useUtm()
watch(utm, () => store.commit('updatedUtm', utm.value), { deep: true, immediate: true })
const router = useRouter()
const mixpanel = useMixpanel()
const gtm = useGtm()

const registration = ref<ServiceWorkerRegistration>()
const showRefreshModal = async (event: CustomEvent<{ registration: ServiceWorkerRegistration }>) => {
  registration.value = event.detail.registration
  modalType.value = grid.breakpoint ? 'overlay' : 'bottomSheet'
  modal.value = 'refresh'
  modalOpen.value = true
}

const analytics = useGWAnalytics()
const { payload } = useGWAnalyticsBasePayload()
const grid = useGrid('tailwind')
const gwModal = useGwModal()
const modalOpen = ref(false)
const modal = ref<'systemDown' | 'refresh' | 'serverOverloaded' | null>(null)
const modalType = ref<'bottomSheet' | 'overlay'>(grid.breakpoint ? 'overlay' : 'bottomSheet')

const showSystemDownModal = async () => {
  modalType.value = grid.breakpoint ? 'overlay' : 'bottomSheet' 
  modal.value = 'systemDown'
  modalOpen.value = true
}
const showServerOverloadedModal = async () => {
  modalType.value = grid.breakpoint ? 'overlay' : 'bottomSheet' 
  modal.value = 'serverOverloaded'
  modalOpen.value = true
}
const onModalDismiss = () => modalOpen.value = false

const refreshing = ref(false)
onMounted(async () => {
  if (gtm) {
    const gtmAnalyticsProvider = new GtmAnalyticsProvider(gtm)
    analytics.tracker.registerProvider('gtm', gtmAnalyticsProvider)
  }

  const mixpanelAnalyticsProvider = new MixpanelAnalyticsProvider(mixpanel)
  analytics.tracker.registerProvider('mixpanel', mixpanelAnalyticsProvider)

  const gwAnalyticsProvider = new GwCustomAnalyticsProvider()
  analytics.tracker.registerProvider('gwAnalytics', gwAnalyticsProvider)
  analytics.track('session')

  // alert(navigator.userAgent)
  document.addEventListener('gwSwUpdated', showRefreshModal)
  document.addEventListener('gwServerError', showSystemDownModal)
  document.addEventListener('gwServerOverloaded', showServerOverloadedModal)
  document.addEventListener('gwModalPresented', (event) => {
    try {
      analytics.track('page_view', {
        ...payload.value,
        page: typeof event.detail === 'string' ? event.detail : event.detail.page_name,
      })
    } catch(err) {
      console.error(err)
    }
  })
  document.addEventListener('gwModalDismissedByBackdrop', () => {
    try {
      gtm?.trackEvent({
        action: 'click',
        label: 'Modal dismissed by ESC/backdrop'
      })
      mixpanel.buttonClick({
        ...payload.value,
        name: 'ESC'
      })
    } catch(err) {
      console.error(err)
    }
  })

  if (navigator.serviceWorker) {
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshing.value) return
      refreshing.value = true
      window.location.reload()
    })
  }
  await nextTick()
  setLocale(locale.value)
  
  const htmlClassList = document.documentElement.classList
  if (htmlClassList.contains('plt-ios')) store.commit('detectedIosPwa', true)

  if (store.getters.isLoggedIn) {
    try {
      await api.auth.info.get()
    } catch (err) {
      console.error('get info error', err)
    }
  }

  console.log('before query check')
  if (route.query?.reminder) {
    analytics.track('click', {
      ...payload.value,
      name: 'Email reminder'
    })
  }

  mixpanel.pageView(payload.value)

  router.afterEach(() => mixpanel.pageView(payload.value))
  // document.dispatchEvent(new CustomEvent('gwServerOverloaded', { detail: null }))
})

onUnmounted(() => {
  document.removeEventListener('gwSwUpdated', showRefreshModal)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(IonApp), null, {
    default: _withCtx(() => [
      (_ctx.$route.name && ['SetPassword', 'CancelOrder', 'EvaluateOrder', 'NotFound' ].indexOf(_ctx.$route.name as string) === -1 )
        ? (_openBlock(), _createBlock(GwHeader, { key: 0 }))
        : _createCommentVNode("", true),
      _createVNode(_unref(IonRouterOutlet)),
      _createVNode(_unref(IonModal), {
        animated: "true",
        class: _normalizeClass(modalType.value === 'bottomSheet' ? 'gw-modal-sheet' : '-transparent'),
        "enter-animation": modalType.value === 'overlay' ? _unref(gwModal).enterAnimationDefinition : undefined,
        "leave-animation": modalType.value === 'overlay' ? _unref(gwModal).leaveAnimationDefinition : undefined,
        "is-open": modalOpen.value,
        breakpoints: modalType.value === 'bottomSheet' ? [0,1] : undefined,
        "initial-breakpoint": modalType.value === 'bottomSheet' ? 1 : undefined,
        handle: false,
        "can-dismiss": false,
        onDidDismiss: onModalDismiss
      }, {
        default: _withCtx(() => [
          (_openBlock(), _createBlock(_resolveDynamicComponent(modalType.value === 'bottomSheet' ? GwBottomSheet : GwModal), {
            "can-dismiss": false,
            "css-class": modalType.value === 'overlay' ? '-transparent' : '',
            name: modal.value === 'systemDown' ? 'SystemDownModal' : 'RefreshAppModal'
          }, {
            default: _withCtx(() => [
              (modal.value === 'systemDown')
                ? (_openBlock(), _createBlock(SystemDownModal, { key: 0 }))
                : (modal.value === 'serverOverloaded')
                  ? (_openBlock(), _createBlock(ServerOverloaded, { key: 1 }))
                  : (modal.value === 'refresh' && registration.value)
                    ? (_openBlock(), _createBlock(RefreshApp, {
                        key: 2,
                        registration: registration.value
                      }, null, 8, ["registration"]))
                    : _createCommentVNode("", true)
            ]),
            _: 1
          }, 8, ["css-class", "name"]))
        ]),
        _: 1
      }, 8, ["class", "enter-animation", "leave-animation", "is-open", "breakpoints", "initial-breakpoint"]),
      _createVNode(LoginModal),
      _createVNode(AddToCalendarModal),
      _createVNode(MenuModal)
    ]),
    _: 1
  }))
}
}

})